import { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { CheckCircle2, XCircle, FileText } from "lucide-react";

// Public customer authorization page — reached via /authorize/:bookingId?token=<tracking_token>
export default function Authorize() {
  const { bookingId } = useParams();
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [signedName, setSignedName] = useState("");
  const [submitted, setSubmitted] = useState(null);

  useEffect(() => { (async () => {
    if (!token) { setError("Missing token — please open this page from the link in your email."); return; }
    try { const { data } = await api.get(`/bookings/${bookingId}/final-estimate`, { params: { token } }); setData(data); }
    catch (e) { setError(e?.response?.data?.detail || "Could not load your estimate."); }
  })(); /* eslint-disable-next-line */ }, [bookingId, token]);

  const decide = async (decision) => {
    if (!signedName.trim()) { toast.error("Please type your full name to sign."); return; }
    try {
      const { data: r } = await api.post(`/bookings/${bookingId}/authorize`, { token, decision, signed_name: signedName.trim() });
      setSubmitted(r.status);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  if (error) return <div className="pt-24 pb-16 max-w-xl mx-auto px-4 text-center"><h1 className="text-2xl font-bold text-rose-600">Can't load estimate</h1><p className="mt-3 text-slate-600">{error}</p></div>;
  if (!data) return <div className="pt-32 text-center">Loading…</div>;
  const { booking, estimate } = data;

  if (submitted === "authorized") return (
    <div className="pt-24 pb-16 max-w-xl mx-auto px-4 text-center">
      <CheckCircle2 className="w-16 h-16 text-emerald-600 mx-auto" />
      <h1 className="mt-4 text-3xl font-bold">Repair authorized</h1>
      <p className="mt-3 text-slate-600">Thanks — we'll begin work on your {booking.brand} {booking.model_name} right away and email you when it's ready to pick up.</p>
    </div>
  );
  if (submitted === "declined") return (
    <div className="pt-24 pb-16 max-w-xl mx-auto px-4 text-center">
      <XCircle className="w-16 h-16 text-slate-600 mx-auto" />
      <h1 className="mt-4 text-3xl font-bold">Estimate declined</h1>
      <p className="mt-3 text-slate-600">No problem — we'll contact you to arrange pickup of your unrepaired device.</p>
    </div>
  );

  return (
    <div className="pt-24 pb-16 max-w-2xl mx-auto px-4">
      <div className="text-xs uppercase tracking-widest text-amber-700 font-semibold">Awaiting your approval</div>
      <h1 className="mt-1 text-3xl font-bold text-slate-900">Final Written Repair Estimate</h1>
      <p className="mt-2 text-slate-600">Ticket <span className="font-mono font-bold text-slate-900">{booking.ticket_code}</span> · {booking.brand} {booking.model_name}</p>
      <div className="mt-6 p-6 rounded-2xl border border-slate-200 bg-white">
        <div className="mb-3"><div className="overline">Diagnosis</div><p className="text-sm">{estimate.diagnosis}</p></div>
        <table className="w-full text-sm mb-3">
          <thead><tr className="text-left text-xs uppercase text-slate-500"><th className="py-2">Item</th><th>Part quality</th><th className="text-right">Parts</th><th className="text-right">Labor</th></tr></thead>
          <tbody>
            {estimate.lines.map((l, i) => (
              <tr key={i} className="border-t border-slate-100">
                <td className="py-2">{l.label}</td>
                <td className="text-xs text-slate-500">{(l.part_quality || "").replace(/_/g, " ").toLowerCase()}</td>
                <td className="text-right font-mono">${(l.parts_cost || 0).toFixed(2)}</td>
                <td className="text-right font-mono">${(l.labor_cost || 0).toFixed(2)}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <div className="text-sm space-y-1 border-t border-slate-200 pt-3">
          <div className="flex justify-between"><span>Parts</span><span className="font-mono">${estimate.totals.parts_total.toFixed(2)}</span></div>
          <div className="flex justify-between"><span>Labor</span><span className="font-mono">${estimate.totals.labor_total.toFixed(2)}</span></div>
          <div className="flex justify-between"><span>Estimated sales tax</span><span className="font-mono">${estimate.totals.tax_total.toFixed(2)}</span></div>
          <div className="flex justify-between font-bold pt-2 border-t border-slate-100"><span>Estimated total</span><span className="font-mono">${estimate.totals.grand_total.toFixed(2)}</span></div>
        </div>
      </div>
      <div className="mt-6 p-6 rounded-2xl border border-slate-200 bg-white">
        <p className="text-sm text-slate-700 mb-3">I authorize The Repair Nook to perform the repairs specifically listed in this written estimate for the stated authorized amount. I understand that additional repairs or charges beyond this authorized estimate require additional authorization.</p>
        <label className="block text-sm">
          <span className="text-xs text-slate-500 uppercase">Type your full name to sign</span>
          <input data-testid="signed-name" className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2" value={signedName} onChange={(e) => setSignedName(e.target.value)} />
        </label>
        <div className="mt-4 flex gap-3">
          <button data-testid="btn-authorize" onClick={() => decide("authorize")} className="flex-1 inline-flex items-center justify-center gap-2 px-5 py-3 rounded-xl bg-slate-900 text-white font-semibold hover:bg-slate-800">
            <CheckCircle2 className="w-4 h-4" /> Authorize repair
          </button>
          <button data-testid="btn-decline" onClick={() => decide("decline")} className="flex-1 inline-flex items-center justify-center gap-2 px-5 py-3 rounded-xl border border-slate-200 bg-white text-slate-800 font-semibold hover:border-slate-400">
            <XCircle className="w-4 h-4" /> Decline
          </button>
        </div>
      </div>
    </div>
  );
}
